import { type HexString, type IssuedClaim, parseBigInt, toHex } from '@coinweb/dapp-conventions';

import { type NetworkName } from '../../constants/networkName';

const SNAPSHOT_SHIFT = '0x0';
const SERDE_JSON_NUMBER_KEY = '$serde_json::private::Number';

const FETCH_CLAIMS_STABLE_V1_QUERY = `
query FetchByReadOp($network: NetworkName!, $readOps: [GqlCwebReadOp!]!, $snapshotShift: BigInt!) {
  shard(name: $network) {
    snapshots {
      head(shift: $snapshotShift) {
        fetchClaimsStableV1(readOps: $readOps) {
          claim {
            issuer
            content {
              key
              body
              feesStored
            }
          }
        }
      }
    }
  }
}
`.trim();

export type StableReadOpClaim = {
  issuer: { FromSmartContract: string };
  keyFirstPart: unknown;
  keySecondPart?: unknown;
};

export type StableReadOpClaimRange = {
  issuer: { FromSmartContract: string };
  keyFirstPart: unknown;
  startsAtKeySecondPart?: unknown;
  endsAtKeySecondPart?: unknown;
  maxCount: number;
};

type ReadOp = { claim: StableReadOpClaim } | { claimRange: StableReadOpClaimRange };

type WalletClaim = IssuedClaim & { content: { feesStored: unknown } };

type WalletGraphqlResponse = {
  data?: {
    shard?: {
      snapshots?: {
        head?: {
          fetchClaimsStableV1?: Array<{
            claim?: WalletClaim | null;
          } | null> | null;
        } | null;
      } | null;
    } | null;
  };
  errors?: Array<{ message?: string }>;
};

const getWalletUrl = (): string => {
  const configuredUrl = import.meta.env.VITE_BACKEND_URL;
  const walletUrl = typeof configuredUrl === 'string' ? configuredUrl.trim() : '';

  if (!walletUrl) {
    throw new Error('VITE_BACKEND_URL is not defined');
  }

  return walletUrl;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseSerdeJsonNumber = (value: unknown): unknown => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      return value;
    }

    const parsedNumber = Number(trimmed);

    if (!Number.isFinite(parsedNumber)) {
      return value;
    }

    if (/^-?\d+$/.test(trimmed) && !Number.isSafeInteger(parsedNumber)) {
      return value;
    }

    return parsedNumber;
  }

  return value;
};

const normalizeSerdeJsonNumbers = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeSerdeJsonNumbers(item));
  }

  if (!isRecord(value)) {
    return value;
  }

  const keys = Object.keys(value);

  if (keys.length === 1 && keys[0] === SERDE_JSON_NUMBER_KEY) {
    return parseSerdeJsonNumber(value[SERDE_JSON_NUMBER_KEY]);
  }

  const normalized: Record<string, unknown> = {};

  for (const [key, nestedValue] of Object.entries(value)) {
    normalized[key] = normalizeSerdeJsonNumbers(nestedValue);
  }

  return normalized;
};

const toFeesStoredHex = (feesStored: unknown): HexString => {
  if (typeof feesStored !== 'bigint' && typeof feesStored !== 'string' && typeof feesStored !== 'number') {
    throw new Error('Invalid feesStored');
  }

  const parsed = parseBigInt(feesStored);

  if ('error' in parsed) {
    throw new Error(`Invalid feesStored: ${parsed.error}`);
  }

  return toHex(parsed.ok);
};

const toIssuedClaim = (claim: WalletClaim): IssuedClaim => {
  return {
    ...claim,
    content: {
      ...claim.content,
      fees_stored: toFeesStoredHex(claim.content.feesStored),
    },
  };
};

const postReadOps = async (networkName: NetworkName, readOps: ReadOp[]): Promise<WalletClaim[]> => {
  const response = await fetch(getWalletUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: FETCH_CLAIMS_STABLE_V1_QUERY,
      variables: {
        network: networkName,
        readOps,
        snapshotShift: SNAPSHOT_SHIFT,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Wallet GraphQL request failed (${response.status})`);
  }

  const payload = normalizeSerdeJsonNumbers(await response.json()) as WalletGraphqlResponse;

  if (payload.errors?.length) {
    const errorMessage = payload.errors
      .map((error) => error.message)
      .filter(Boolean)
      .join('; ');

    throw new Error(`Wallet GraphQL returned errors: ${errorMessage || 'unknown error'}`);
  }

  return (payload.data?.shard?.snapshots?.head?.fetchClaimsStableV1 ?? []).flatMap((item) =>
    item?.claim ? [item.claim] : []
  );
};

export const walletServiceGraphQlClient = {
  fetchClaim: async (networkName: NetworkName, claim: StableReadOpClaim): Promise<IssuedClaim | null> => {
    const claims = await postReadOps(networkName, [{ claim }]);
    const issuedClaim = claims[0];

    return issuedClaim ? toIssuedClaim(issuedClaim) : null;
  },

  fetchClaims: async (networkName: NetworkName, claimRange: StableReadOpClaimRange): Promise<IssuedClaim[]> => {
    const claims = await postReadOps(networkName, [{ claimRange }]);

    return claims.map(toIssuedClaim);
  },
};
